import type { PlatformAdapter, ToastParams, PopupParams, ShareData, GeoPosition, GeoOptions, MediaConstraints } from "@cogo/runtime-v2"

/**
 * WebPlatformAdapter — browser implementation of PlatformAdapter.
 * Wraps all browser APIs (window, document, navigator, localStorage).
 */
export class WebPlatformAdapter implements PlatformAdapter {
  private toastHandler: ((p: ToastParams) => void) | null = null
  private popupHandler: ((p: PopupParams) => void) | null = null
  private loadingHandler: ((show: boolean) => void) | null = null
  private fallbackStorage = new Map<string, string>()

  setToastHandler(handler: (p: ToastParams) => void): void {
    this.toastHandler = handler
  }

  setPopupHandler(handler: (p: PopupParams) => void): void {
    this.popupHandler = handler
  }

  setLoadingHandler(handler: (show: boolean) => void): void {
    this.loadingHandler = handler
  }

  // -- Navigation --
  pushRoute(path: string): void {
    if (typeof window === "undefined") return
    window.history.pushState({}, "", path)
  }

  replaceRoute(path: string): void {
    if (typeof window === "undefined") return
    window.history.replaceState({}, "", path)
  }

  goBack(): void {
    if (typeof window === "undefined") return
    window.history.back()
  }

  openExternal(url: string, newTab = true): void {
    if (typeof window === "undefined") return
    if (newTab) {
      window.open(url, "_blank", "noopener,noreferrer")
    } else {
      window.location.href = url
    }
  }

  getCurrentPath(): string {
    if (typeof window === "undefined") return "/"
    return window.location.pathname + window.location.search
  }

  // -- Storage --
  getItem(key: string): string | null {
    const storage = this.getLocalStorage()
    if (!storage) return this.fallbackStorage.get(key) ?? null
    try {
      return storage.getItem(key)
    } catch {
      return this.fallbackStorage.get(key) ?? null
    }
  }

  setItem(key: string, value: string): void {
    const storage = this.getLocalStorage()
    if (storage) {
      try {
        storage.setItem(key, value)
        return
      } catch {
        // Fall through to memory storage for restricted or mocked environments.
      }
    }
    this.fallbackStorage.set(key, value)
  }

  removeItem(key: string): void {
    const storage = this.getLocalStorage()
    if (storage) {
      try {
        storage.removeItem(key)
      } catch {
        // Keep fallback cleanup deterministic even when browser storage throws.
      }
    }
    this.fallbackStorage.delete(key)
  }

  clearStorage(): void {
    const storage = this.getLocalStorage()
    if (storage) {
      try {
        storage.clear()
      } catch {
        // Keep fallback cleanup deterministic even when browser storage throws.
      }
    }
    this.fallbackStorage.clear()
  }

  private getLocalStorage(): Pick<Storage, "getItem" | "setItem" | "removeItem" | "clear"> | null {
    if (typeof globalThis === "undefined") return null
    const storage = (globalThis as { localStorage?: Partial<Storage> }).localStorage
    if (!storage) return null
    if (
      typeof storage.getItem !== "function" ||
      typeof storage.setItem !== "function" ||
      typeof storage.removeItem !== "function" ||
      typeof storage.clear !== "function"
    ) {
      return null
    }
    return storage as Pick<Storage, "getItem" | "setItem" | "removeItem" | "clear">
  }

  // -- Clipboard --
  async copyToClipboard(text: string): Promise<void> {
    if (typeof navigator !== "undefined" && navigator.clipboard) {
      await navigator.clipboard.writeText(text)
      return
    }
    // Fallback for older browsers
    if (typeof document === "undefined") return
    const textarea = document.createElement("textarea")
    textarea.value = text
    textarea.style.position = "fixed"
    textarea.style.opacity = "0"
    document.body.appendChild(textarea)
    textarea.select()
    document.execCommand("copy")
    document.body.removeChild(textarea)
  }

  async readClipboard(): Promise<string> {
    if (typeof navigator !== "undefined" && navigator.clipboard) {
      return navigator.clipboard.readText()
    }
    return ""
  }

  // -- UI Feedback --
  showToast(params: ToastParams): void {
    this.toastHandler?.(params)
  }

  showPopup(params: PopupParams): void {
    this.popupHandler?.(params)
  }

  showLoading(show: boolean): void {
    this.loadingHandler?.(show)
  }

  // -- Scroll --
  scrollToElement(id: string, smooth = true): void {
    if (typeof document === "undefined") return
    const addressed = document.getElementById(id) ?? Array.from(
      document.querySelectorAll<HTMLElement>("[data-cogo-element-id], [data-id]"),
    ).find((candidate) =>
      candidate.getAttribute("data-cogo-element-id") === id ||
      candidate.getAttribute("data-id") === id,
    )
    const target = addressed?.matches?.(".contents")
      ? addressed.querySelector<HTMLElement>(
        "input, textarea, select, button, a[href], [role], [tabindex], [id], *",
      ) ?? addressed
      : addressed
    target?.scrollIntoView({ behavior: smooth ? "smooth" : "auto" })
  }

  scrollToPosition(y: number, smooth = true): void {
    if (typeof window === "undefined") return
    window.scrollTo({ top: y, behavior: smooth ? "smooth" : "auto" })
  }

  getScrollY(): number {
    if (typeof window === "undefined") return 0
    return window.scrollY
  }

  // -- Theming -- (Lane B-2: setTheme applies a named theme on the document root)
  setTheme(theme: string): void {
    if (typeof document === "undefined") return
    document.documentElement.setAttribute("data-theme", theme)
  }

  // -- System --
  print(): void {
    if (typeof window === "undefined") return
    window.print()
  }

  async share(data: ShareData): Promise<void> {
    if (typeof navigator === "undefined" || !navigator.share) return
    // Web Share API L2: convert file descriptors to File objects and only include
    // them when the platform can actually share files (navigator.canShare).
    const { files, ...meta } = data
    if (Array.isArray(files) && files.length > 0) {
      const fileObjs = files.map((f) => toShareFile(f)).filter((f): f is File => f != null)
      const payload = { ...meta, files: fileObjs }
      const canShare = (navigator as unknown as { canShare?: (d: unknown) => boolean }).canShare
      if (fileObjs.length > 0 && (!canShare || canShare(payload))) {
        await navigator.share(payload)
        return
      }
    }
    await navigator.share(meta)
  }

  async getGeolocation(options?: GeoOptions): Promise<GeoPosition> {
    if (typeof navigator === "undefined" || !navigator.geolocation) {
      return { latitude: 0, longitude: 0 }
    }
    return new Promise((resolve, reject) => {
      navigator.geolocation.getCurrentPosition(
        (pos) => resolve({
          latitude: pos.coords.latitude,
          longitude: pos.coords.longitude,
          accuracy: pos.coords.accuracy,
        }),
        (err) => reject(new Error(err.message)),
        options, // PositionOptions { enableHighAccuracy?, timeout? }
      )
    })
  }

  async getUserMedia(constraints: MediaConstraints): Promise<unknown> {
    if (typeof navigator === "undefined" || !navigator.mediaDevices) return null
    const { source, ...native } = constraints
    if (source === "screen") {
      const md = navigator.mediaDevices as MediaDevices & { getDisplayMedia?: (c: unknown) => Promise<MediaStream> }
      if (md.getDisplayMedia) return md.getDisplayMedia(native)
    }
    return navigator.mediaDevices.getUserMedia(native as MediaStreamConstraints)
  }

  download(url: string, filename: string): void {
    if (typeof document === "undefined") return
    const a = document.createElement("a")
    a.href = url
    a.download = filename
    a.style.display = "none"
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
  }

  async pickFile(accept?: string): Promise<{ name: string; data: unknown } | null> {
    if (typeof document === "undefined") return null
    return new Promise((resolve) => {
      const input = document.createElement("input")
      input.type = "file"
      if (accept) input.accept = accept
      input.onchange = () => {
        const file = input.files?.[0]
        if (file) {
          resolve({ name: file.name, data: file })
        } else {
          resolve(null)
        }
        document.body.removeChild(input)
      }
      input.style.display = "none"
      document.body.appendChild(input)
      input.click()
    })
  }

  async pickFiles(accept?: string, multiple = true): Promise<Array<{ name: string; data: unknown }>> {
    if (typeof document === "undefined") return []
    return new Promise((resolve) => {
      const input = document.createElement("input")
      input.type = "file"
      if (accept) input.accept = accept
      input.multiple = multiple
      input.onchange = () => {
        const files = input.files
          ? Array.from(input.files).map((file) => ({ name: file.name, data: file }))
          : []
        resolve(files)
        document.body.removeChild(input)
      }
      input.style.display = "none"
      document.body.appendChild(input)
      input.click()
    })
  }

  showNotification(title: string, body?: string): void {
    if (typeof Notification === "undefined") return
    if (Notification.permission === "granted") {
      new Notification(title, { body })
    } else if (Notification.permission !== "denied") {
      Notification.requestPermission().then((perm) => {
        if (perm === "granted") new Notification(title, { body })
      })
    }
  }

  async digest(algorithm: string, data: string): Promise<string> {
    if (typeof globalThis.crypto?.subtle === "undefined") return ""
    const encoder = new TextEncoder()
    const buf = await globalThis.crypto.subtle.digest(algorithm, encoder.encode(data))
    return Array.from(new Uint8Array(buf)).map((b) => b.toString(16).padStart(2, "0")).join("")
  }

  async fetch(url: string, options?: RequestInit): Promise<Response> {
    return globalThis.fetch(url, options)
  }

  createEventSource(url: string, onMessage: (data: string) => void): { close(): void } {
    if (typeof EventSource === "undefined") return { close() {} }
    const es = new EventSource(url)
    es.onmessage = (event) => onMessage(event.data)
    return { close: () => es.close() }
  }

  createWebSocket(url: string) {
    const ws = new WebSocket(url)
    return {
      send(data: string) { ws.send(data) },
      close() { ws.close() },
      onMessage(handler: (data: unknown) => void) { ws.onmessage = (e) => handler(e.data) },
      onClose(handler: () => void) { ws.onclose = handler },
    }
  }

  dispatchAnalyticsEvent(event: string, data?: Record<string, unknown>): void {
    if (typeof CustomEvent !== "undefined") {
      globalThis.dispatchEvent(new CustomEvent("cogo:analytics", {
        detail: { event, ...data },
      }))
    }
  }

  /**
   * Open OAuth auth session via popup window.
   * Opens a popup, polls for redirect URL, returns the result.
   */
  async openAuthSession(
    url: string,
    redirectUrl?: string,
  ): Promise<{ type: string; url?: string }> {
    if (typeof window === "undefined") return { type: "error" }

    return new Promise((resolve) => {
      const width = 500
      const height = 700
      const left = window.screenX + (window.innerWidth - width) / 2
      const top = window.screenY + (window.innerHeight - height) / 2
      const popup = window.open(
        url,
        "cogo-auth",
        `width=${width},height=${height},left=${left},top=${top},toolbar=no,menubar=no`,
      )

      if (!popup) {
        resolve({ type: "error" })
        return
      }

      // Poll for redirect or close
      const pollInterval = setInterval(() => {
        try {
          if (popup.closed) {
            clearInterval(pollInterval)
            resolve({ type: "cancel" })
            return
          }
          const currentUrl = popup.location.href
          if (redirectUrl && currentUrl.startsWith(redirectUrl)) {
            clearInterval(pollInterval)
            popup.close()
            resolve({ type: "success", url: currentUrl })
          }
        } catch {
          // Cross-origin — continue polling
        }
      }, 500)

      // Timeout after 5 minutes
      setTimeout(() => {
        clearInterval(pollInterval)
        if (!popup.closed) popup.close()
        resolve({ type: "cancel" })
      }, 5 * 60 * 1000)
    })
  }

  /**
   * Keyed / symmetric crypto via WebCrypto (cryptoAction.operation/key).
   *
   * Security model (documented, client-side convenience — NOT a substitute for
   * server-side key management):
   *   - sign / hmac : HMAC-SHA256(key, data) → base64 (the key is used directly; HMAC
   *                   is a keyed MAC, not password-based, so no KDF is applied).
   *   - encrypt     : AES-256-GCM with a key derived from the passphrase via PBKDF2
   *                   (HMAC-SHA256, 600,000 iterations — OWASP 2023 minimum — with a
   *                   random 16-byte salt). A random 12-byte IV is used; output =
   *                   base64(salt(16) || iv(12) || ciphertext).
   *   - decrypt     : the reverse — salt + IV are parsed from the prefix and the key
   *                   re-derived; AES-GCM authentication rejects tampered ciphertext.
   * Unknown operations throw (surfaced by the runtime as `{ error: "crypto_failed" }`).
   */
  async cryptoOperation(operation: string, _algorithm: string, data: string, key?: string): Promise<string> {
    const subtle = globalThis.crypto?.subtle
    if (!subtle) throw new Error("subtlecrypto_unavailable")
    const enc = new TextEncoder()
    switch (operation) {
      case "sign":
      case "hmac": {
        if (!key) throw new Error("crypto_key_required")
        const k = await subtle.importKey("raw", enc.encode(key), { name: "HMAC", hash: "SHA-256" }, false, ["sign"])
        return abToBase64(await subtle.sign("HMAC", k, enc.encode(data)))
      }
      case "encrypt": {
        if (!key) throw new Error("crypto_key_required")
        const salt = globalThis.crypto.getRandomValues(new Uint8Array(16))
        const iv = globalThis.crypto.getRandomValues(new Uint8Array(12))
        const k = await deriveAesGcmKey(subtle, key, salt, ["encrypt"])
        const ct = new Uint8Array(await subtle.encrypt({ name: "AES-GCM", iv }, k, enc.encode(data)))
        const out = new Uint8Array(salt.length + iv.length + ct.length)
        out.set(salt, 0)
        out.set(iv, salt.length)
        out.set(ct, salt.length + iv.length)
        return abToBase64(out.buffer)
      }
      case "decrypt": {
        if (!key) throw new Error("crypto_key_required")
        const all = base64ToBytes(data)
        if (all.length < 28) throw new Error("crypto_ciphertext_too_short") // 16 salt + 12 iv minimum
        const salt = all.slice(0, 16)
        const iv = all.slice(16, 28)
        const ct = all.slice(28)
        const k = await deriveAesGcmKey(subtle, key, salt, ["decrypt"])
        const pt = await subtle.decrypt({ name: "AES-GCM", iv }, k, ct)
        return new TextDecoder().decode(pt)
      }
      default:
        throw new Error(`crypto_unsupported_operation: ${operation}`)
    }
  }

  /**
   * Time-boxed media recording (mediaCapture.maxDuration). Acquires the stream
   * (honoring source/quality), records via MediaRecorder for maxDurationMs, then
   * stops the tracks and resolves the recorded Blob. Returns null when MediaRecorder
   * is unavailable (e.g. SSR / unsupported browser).
   */
  async recordMedia(constraints: MediaConstraints, maxDurationMs: number): Promise<unknown> {
    const stream = await this.getUserMedia(constraints)
    if (!stream) return null
    const mediaStream = stream as MediaStream
    // If recording is unavailable after the stream was acquired, RELEASE the live
    // tracks before bailing — otherwise the camera/mic stays on with no handle to
    // stop it (the OS capture indicator never clears).
    if (typeof MediaRecorder === "undefined") {
      mediaStream.getTracks().forEach((t) => t.stop())
      return null
    }
    return new Promise((resolve, reject) => {
      const chunks: Blob[] = []
      let rec: MediaRecorder
      try {
        rec = new MediaRecorder(mediaStream)
      } catch (e) {
        mediaStream.getTracks().forEach((t) => t.stop())
        reject(e)
        return
      }
      rec.ondataavailable = (e) => { if (e.data && e.data.size > 0) chunks.push(e.data) }
      rec.onstop = () => {
        mediaStream.getTracks().forEach((t) => t.stop())
        resolve(new Blob(chunks, { type: rec.mimeType || "video/webm" }))
      }
      rec.onerror = () => {
        mediaStream.getTracks().forEach((t) => t.stop())
        reject(new Error("recording_failed"))
      }
      rec.start()
      setTimeout(() => { if (rec.state !== "inactive") rec.stop() }, Math.max(0, maxDurationMs))
    })
  }
}

/** PBKDF2 iteration count for password-based AES key derivation (OWASP 2023 minimum for HMAC-SHA256). */
const PBKDF2_ITERATIONS = 600_000

/** Derive a 256-bit AES-GCM key from a passphrase via PBKDF2-HMAC-SHA256 + salt. */
async function deriveAesGcmKey(
  subtle: SubtleCrypto,
  passphrase: string,
  salt: Uint8Array,
  usages: KeyUsage[],
): Promise<CryptoKey> {
  const base = await subtle.importKey("raw", new TextEncoder().encode(passphrase), "PBKDF2", false, ["deriveKey"])
  return subtle.deriveKey(
    { name: "PBKDF2", salt: salt as BufferSource, iterations: PBKDF2_ITERATIONS, hash: "SHA-256" },
    base,
    { name: "AES-GCM", length: 256 },
    false,
    usages,
  )
}

/** ArrayBuffer → base64 (browser btoa). */
function abToBase64(buf: ArrayBuffer): string {
  const bytes = new Uint8Array(buf)
  let bin = ""
  for (let i = 0; i < bytes.length; i++) bin += String.fromCharCode(bytes[i])
  return btoa(bin)
}

/** base64 → bytes (browser atob). */
function base64ToBytes(b64: string): Uint8Array {
  const bin = atob(b64)
  const bytes = new Uint8Array(bin.length)
  for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i)
  return bytes
}

/**
 * Convert a share-file descriptor to a File for the Web Share API L2. Accepts an
 * actual File/Blob, or a `{ name, type, data }` descriptor whose `data` is a data:
 * URL, raw string, or ArrayBuffer/view. Returns null for unconvertible input.
 */
function toShareFile(f: unknown): File | null {
  if (typeof File !== "undefined" && f instanceof File) return f
  if (typeof Blob !== "undefined" && f instanceof Blob) return new File([f], "file")
  if (!f || typeof f !== "object") return null
  const o = f as { name?: unknown; type?: unknown; data?: unknown }
  const name = o.name != null ? String(o.name) : "file"
  const type = o.type != null ? String(o.type) : undefined
  const d = o.data
  let parts: BlobPart[]
  if (typeof d === "string") {
    if (d.startsWith("data:")) {
      const comma = d.indexOf(",")
      const meta = d.slice(5, comma)
      const body = d.slice(comma + 1)
      if (/;base64$/i.test(meta)) {
        const bin = atob(body)
        const bytes = new Uint8Array(bin.length)
        for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i)
        parts = [bytes]
      } else {
        parts = [decodeURIComponent(body)]
      }
    } else {
      parts = [d]
    }
  } else if (d instanceof ArrayBuffer || ArrayBuffer.isView(d as ArrayBufferView)) {
    parts = [d as BlobPart]
  } else {
    return null
  }
  return new File(parts, name, type ? { type } : undefined)
}
